/**
 * 甘特图时间轴组件
 *
 * 显示时间轴头部（日/周/月刻度）、今日线和网格背景
 */

import { isWeekend, addDays, format } from 'date-fns'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { DependencyLine } from './dependency-line'
import { MilestoneMarker } from './milestone-marker'
import { StageTimeRangeBar } from './stage-time-range-bar'
import { TaskBar } from './task-bar'
import { DEFAULT_ROW_HEIGHT } from './types'
import {
  generateTimelineMarkers,
  calculateTodayLinePosition,
  getColumnWidth,
  calculateTimelineWidth,
} from './utils'

import type {
  DateRange,
  ZoomLevel,
  GanttTask,
  GanttMilestone,
  TaskGroup,
  GroupByOption,
  TaskDates,
  DragEdge,
} from './types'

interface GanttTimelineProps {
  /** 任务列表 */
  tasks: GanttTask[]
  /** 分组数据 */
  groups: TaskGroup[]
  /** 分组方式 */
  groupBy: GroupByOption
  /** 里程碑列表 */
  milestones: GanttMilestone[]
  /** 缩放级别 */
  zoomLevel: ZoomLevel
  /** 日期范围 */
  dateRange: DateRange
  /** 任务拖拽回调 */
  onTaskDrag: (taskId: string, newDates: TaskDates) => void
  /** 任务点击回调 */
  onTaskClick: (taskId: string) => void
  /** 里程碑点击回调 */
  onMilestoneClick: (milestoneId: string) => void
  /** 是否显示依赖线 */
  showDependencies: boolean
  /** 行高 */
  rowHeight?: number
  /** 滚动位置同步 */
  scrollTop: number
  /** 滚动回调 */
  onScroll: (scrollTop: number, scrollLeft: number) => void
}

export function GanttTimeline({
  tasks,
  groups,
  groupBy,
  milestones,
  zoomLevel,
  dateRange,
  onTaskDrag,
  onTaskClick,
  onMilestoneClick,
  showDependencies,
  rowHeight = DEFAULT_ROW_HEIGHT,
  scrollTop,
  onScroll,
}: GanttTimelineProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [draggedTaskId, setDraggedTaskId] = React.useState<string | null>(null)
  const [dragDeltaX, setDragDeltaX] = React.useState(0)
  const [dragEdge, setDragEdge] = React.useState<DragEdge | null>(null)

  // 生成时间轴刻度
  const markers = React.useMemo(
    () => generateTimelineMarkers(dateRange, zoomLevel),
    [dateRange, zoomLevel]
  )

  // 计算今日线位置
  const todayLinePosition = React.useMemo(
    () => calculateTodayLinePosition(dateRange, zoomLevel),
    [dateRange, zoomLevel]
  )

  // 计算时间轴总宽度
  const timelineWidth = React.useMemo(
    () => calculateTimelineWidth(dateRange, zoomLevel),
    [dateRange, zoomLevel]
  )

  // 获取要显示的任务列表（考虑分组）
  const displayTasks = React.useMemo(() => {
    if (groupBy === 'none') {
      return tasks
    }
    // 分组模式下，只显示展开分组中的任务
    return groups.flatMap((group) => (group.isExpanded ? group.tasks : []))
  }, [tasks, groups, groupBy])

  // 计算依赖线
  const dependencyLines = React.useMemo(() => {
    if (!showDependencies) { return [] }

    const lines: Array<{
      from: { x: number; y: number }
      to: { x: number; y: number }
      parentId: string
      childId: string
    }> = []

    const taskIndexMap = new Map(displayTasks.map((t, i) => [t.id, i]))
    const dayWidth = getColumnWidth(zoomLevel)

    displayTasks.forEach((task, index) => {
      const parentIndex = task.parentId ? taskIndexMap.get(task.parentId) : undefined
      if (task.parentId && parentIndex !== undefined) {
        const parentTask = displayTasks[parentIndex]

        // 计算连线坐标
        const parentEndX =
          ((new Date(parentTask.endDate).getTime() -
            dateRange.start.getTime()) /
            (1000 * 60 * 60 * 24)) *
          dayWidth +
          dayWidth
        const childStartX =
          ((new Date(task.startDate).getTime() - dateRange.start.getTime()) /
            (1000 * 60 * 60 * 24)) *
          dayWidth

        lines.push({
          from: {
            x: parentEndX,
            y: parentIndex * rowHeight + rowHeight / 2,
          },
          to: {
            x: childStartX,
            y: index * rowHeight + rowHeight / 2,
          },
          parentId: task.parentId,
          childId: task.id,
        })
      }
    })

    return lines
  }, [displayTasks, showDependencies, dateRange, zoomLevel, rowHeight])

  // 处理滚动
  const handleScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget
      onScroll(target.scrollTop, target.scrollLeft)
    },
    [onScroll]
  )

  // 同步滚动位置
  React.useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = scrollTop
    }
  }, [scrollTop])

  const dayWidth = getColumnWidth(zoomLevel)

  // 拖拽处理
  const handleDragStart = React.useCallback((taskId: string, edge: DragEdge) => {
    setDraggedTaskId(taskId)
    setDragEdge(edge)
    setDragDeltaX(0)
  }, [])

  const handleDrag = React.useCallback((deltaX: number) => {
    setDragDeltaX(deltaX)
  }, [])

  const handleDragEnd = React.useCallback(() => {
    // 计算拖拽后的新日期
    if (draggedTaskId && dragDeltaX !== 0) {
      const task = displayTasks.find(t => t.id === draggedTaskId)
      if (task) {
        const deltaDays = Math.round(dragDeltaX / dayWidth)
        if (deltaDays !== 0) {
          const startDate = new Date(task.startDate)
          const endDate = new Date(task.endDate)

          let newStartDate: Date
          let newEndDate: Date

          if (dragEdge === 'body') {
            // 整体移动
            newStartDate = addDays(startDate, deltaDays)
            newEndDate = addDays(endDate, deltaDays)
          } else if (dragEdge === 'left') {
            // 调整开始日期
            newStartDate = addDays(startDate, deltaDays)
            newEndDate = endDate
          } else {
            // 调整结束日期
            newStartDate = startDate
            newEndDate = addDays(endDate, deltaDays)
          }

          // 验证结束日期不早于开始日期
          if (newEndDate >= newStartDate) {
            onTaskDrag(draggedTaskId, {
              startDate: format(newStartDate, 'yyyy-MM-dd'),
              endDate: format(newEndDate, 'yyyy-MM-dd'),
            })
          }
        }
      }
    }

    setDraggedTaskId(null)
    setDragEdge(null)
    setDragDeltaX(0)
  }, [draggedTaskId, dragDeltaX, dragEdge, displayTasks, dayWidth, onTaskDrag])

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-auto"
      onScroll={handleScroll}
    >
      <div style={{ width: timelineWidth, minWidth: '100%' }}>
        {/* 时间轴头部 */}
        <TimelineHeader
          markers={markers}
          zoomLevel={zoomLevel}
          dayWidth={dayWidth}
        />

        {/* 任务区域 */}
        <div className="relative" style={{ height: displayTasks.length * rowHeight }}>
          {/* 网格背景 */}
          <GridBackground
            markers={markers}
            zoomLevel={zoomLevel}
            dayWidth={dayWidth}
            height={displayTasks.length * rowHeight}
          />

          {/* 今日线 */}
          {todayLinePosition !== null && (
            <TodayLine
              position={todayLinePosition}
              height={displayTasks.length * rowHeight}
            />
          )}

          {/* 依赖线 */}
          {showDependencies && (
            <svg
              className="pointer-events-none absolute left-0 top-0"
              width={timelineWidth}
              height={displayTasks.length * rowHeight}
            >
              {dependencyLines.map((line, index) => (
                <DependencyLine
                  key={index}
                  from={line.from}
                  to={line.to}
                  isHighlighted={
                    draggedTaskId === line.parentId ||
                    draggedTaskId === line.childId
                  }
                />
              ))}
            </svg>
          )}

          {/* 任务条 */}
          {displayTasks.map((task, index) => (
            <div
              key={task.id}
              className="absolute left-0 right-0"
              style={{ top: index * rowHeight, height: rowHeight }}
            >
              <TaskBar
                task={task}
                zoomLevel={zoomLevel}
                dateRange={dateRange}
                rowHeight={rowHeight}
                isDragging={draggedTaskId === task.id}
                deltaX={draggedTaskId === task.id ? dragDeltaX : 0}
                dragEdge={draggedTaskId === task.id ? dragEdge : null}
                onDragStart={(edge) => handleDragStart(task.id, edge)}
                onDrag={handleDrag}
                onDragEnd={handleDragEnd}
                onClick={() => onTaskClick(task.id)}
              />
              {/* 阶段时间段显示 */}
              {task.stageTimeRanges && task.stageTimeRanges.length > 0 && (
                <StageTimeRangeBar
                  stageTimeRanges={task.stageTimeRanges}
                  zoomLevel={zoomLevel}
                  dateRange={dateRange}
                  rowHeight={rowHeight}
                />
              )}
            </div>
          ))}

          {/* 里程碑标记 */}
          {milestones.map((milestone) => {
            const position =
              ((new Date(milestone.dueDate).getTime() -
                dateRange.start.getTime()) /
                (1000 * 60 * 60 * 24)) *
              dayWidth +
              dayWidth / 2

            return (
              <MilestoneMarker
                key={milestone.id}
                milestone={milestone}
                position={position}
                onClick={() => onMilestoneClick(milestone.id)}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ==================== 子组件 ====================

interface TimelineHeaderProps {
  markers: Array<{ date: Date; label: string; isToday: boolean }>
  zoomLevel: ZoomLevel
  dayWidth: number
}

function TimelineHeader({ markers, zoomLevel, dayWidth }: TimelineHeaderProps) {
  return (
    <div className="sticky top-0 z-10 flex border-b bg-background">
      {markers.map((marker, index) => {
        const width =
          zoomLevel === 'day'
            ? dayWidth
            : zoomLevel === 'week'
              ? dayWidth * 7
              : dayWidth * 30

        return (
          <div
            key={index}
            className={cn(
              'flex-shrink-0 border-r px-1 py-2 text-center text-xs text-muted-foreground',
              marker.isToday && 'bg-primary/10 font-medium text-primary'
            )}
            style={{ width }}
          >
            {marker.label}
          </div>
        )
      })}
    </div>
  )
}

interface GridBackgroundProps {
  markers: Array<{ date: Date; label: string; isToday: boolean }>
  zoomLevel: ZoomLevel
  dayWidth: number
  height: number
}

function GridBackground({
  markers,
  zoomLevel,
  dayWidth,
  height,
}: GridBackgroundProps) {
  return (
    <div className="absolute inset-0 flex" style={{ height }}>
      {markers.map((marker, index) => {
        const width =
          zoomLevel === 'day'
            ? dayWidth
            : zoomLevel === 'week'
              ? dayWidth * 7
              : dayWidth * 30

        const isWeekendDay = zoomLevel === 'day' && isWeekend(marker.date)

        return (
          <div
            key={index}
            className={cn(
              'flex-shrink-0 border-r border-border/50',
              isWeekendDay && 'bg-muted/30',
              marker.isToday && 'bg-primary/5'
            )}
            style={{ width, height }}
          />
        )
      })}
    </div>
  )
}

interface TodayLineProps {
  position: number
  height: number
}

function TodayLine({ position, height }: TodayLineProps) {
  return (
    <div
      className="pointer-events-none absolute top-0 z-20 w-0.5 bg-primary"
      style={{ left: position, height }}
    />
  )
}
